import React, { createContext, useContext, useEffect, useState } from 'react';
import isEqual from 'lodash/isEqual';
import { SortOrder } from '@dealroadshow/uikit/core/components/Table/DataTable/interfaces/SortOrder';
import { IPermissionGroup } from '@/dataroom/domain/vo/permissionGroups/manage/PermissionGroup';

interface ICollectionState {
  page: number,
  perPage: number,
  sortBy: string,
  sortOrder: SortOrder,
  searchString: string,
}

const initialCollectionState: ICollectionState = {
  page: 1,
  perPage: 25,
  sortBy: 'name',
  sortOrder: 'asc',
  searchString: '',
};

const useManagePermissionGroupsState = () => {
  const [isPristine, setIsPristine] = useState<boolean>(true);
  const [selectedPermissionGroups, setSelectedPermissionGroups] = useState<IPermissionGroup[]>([]);
  const [collectionState, setCollectionState] = useState<ICollectionState>(initialCollectionState);

  useEffect(() => {
    setIsPristine(isEqual(collectionState, initialCollectionState));
    setSelectedPermissionGroups([]);
  }, [collectionState]);

  const setPage = (page: number): void => {
    collectionState.page !== page && setCollectionState({
      ...collectionState,
      page,
    });
  };

  const setPerPage = (perPage: number): void => {
    collectionState.perPage !== perPage && setCollectionState({
      ...collectionState,
      perPage,
    });
  };

  const setSort = (sortBy: string, sortOrder: SortOrder): void => {
    (collectionState.sortBy !== sortBy || collectionState.sortOrder !== sortOrder) && setCollectionState({
      ...collectionState,
      sortBy,
      sortOrder,
    });
  };

  const setSearchString = (searchString: string): void => {
    collectionState.searchString !== searchString && setCollectionState({
      ...collectionState,
      searchString,
    });
  };

  const reset = (): void => {
    setCollectionState(initialCollectionState);
    setSelectedPermissionGroups([]);
  };

  return {
    isPristine,
    selectedPermissionGroups,
    page: collectionState.page,
    perPage: collectionState.perPage,
    sortBy: collectionState.sortBy,
    sortOrder: collectionState.sortOrder,
    searchString: collectionState.searchString,
    setSelectedPermissionGroups,
    setPage,
    setPerPage,
    setSort,
    setSearchString,
    reset,
  };
};

type ManagePermissionGroupsStateType = ReturnType<typeof useManagePermissionGroupsState>;

export const ManagePermissionGroupsStateContext = createContext<ManagePermissionGroupsStateType>(null);

export const useManagePermissionGroupsStateContext = () => {
  const context = useContext(ManagePermissionGroupsStateContext);
  if (!context) {
    throw new Error('useManagePermissionGroupsStateContext must be used within a ManagePermissionGroupsStateContext');
  }
  return context;
};

interface IProps {
  children: React.ReactNode,
}

const ManagePermissionGroupsStateContextProvider = ({ children }: IProps) => (
  <ManagePermissionGroupsStateContext.Provider value={ useManagePermissionGroupsState() }>
    { children }
  </ManagePermissionGroupsStateContext.Provider>
);

export default ManagePermissionGroupsStateContextProvider;
